import { z } from 'zod';

import { FeePurposeSchema } from '../fee-purpose/fee-purpose';
import { ProductAudienceSchema } from '../product-audience/product-audience';
import { ProductKeySchema } from '../product-key/product-key';
import { ProductKindSchema } from '../product-kind/product-kind';
import {
  ProductDurationDaysSchema,
  refineProductKind,
  STORE_PRODUCT_ID_MAX_LENGTH,
  SubscriptionProductTranslationsSchema,
  TierKeySchema,
} from '../subscription-product/subscription-product';

const StoreProductIdSchema = z.string().trim().min(1).max(STORE_PRODUCT_ID_MAX_LENGTH).nullable();

/**
 * Authoring a product.
 *
 * `kind` defaults to `subscription` so every request written before D9a still
 * parses as the plan it always was; `purpose`, `tier` and `durationDays` default
 * to absent and the kind refinement then says which of them the request owes.
 * The `kind` is **create-only** — like `key`, it is snapshotted onto every order
 * and entitlement the product ever produced, and changing it would re-describe
 * money already taken.
 */
export const SubscriptionProductCreateSchema = z
  .object({
    key: ProductKeySchema,
    kind: ProductKindSchema.default('subscription'),
    purpose: FeePurposeSchema.nullable().default(null),
    audience: ProductAudienceSchema,
    tier: TierKeySchema.nullable().default(null),
    durationDays: ProductDurationDaysSchema.nullable().default(null),
    iosProductId: StoreProductIdSchema.default(null),
    androidProductId: StoreProductIdSchema.default(null),
    rcEntitlementId: StoreProductIdSchema.default(null),
    isActive: z.boolean().default(false),
    sortOrder: z.number().int().nonnegative().default(0),
    translations: SubscriptionProductTranslationsSchema,
  })
  .strict()
  .superRefine(refineProductKind);

export type SubscriptionProductCreateRequest = z.infer<typeof SubscriptionProductCreateSchema>;

/**
 * Editing a product. `key` and `kind` are absent by construction (see above);
 * the kind-dependent fields cannot be refined here because the kind is not in
 * the body — the api checks the patch against the stored row instead.
 */
export const SubscriptionProductUpdateSchema = z
  .object({
    purpose: FeePurposeSchema.nullable(),
    audience: ProductAudienceSchema,
    tier: TierKeySchema.nullable(),
    durationDays: ProductDurationDaysSchema.nullable(),
    iosProductId: StoreProductIdSchema,
    androidProductId: StoreProductIdSchema,
    rcEntitlementId: StoreProductIdSchema,
    isActive: z.boolean(),
    sortOrder: z.number().int().nonnegative(),
    translations: SubscriptionProductTranslationsSchema,
  })
  .partial()
  .strict();

export type SubscriptionProductUpdateRequest = z.infer<typeof SubscriptionProductUpdateSchema>;
